const BASE = '/api';

export type ResponseKind = 'json' | 'text' | 'binary';

interface ApiErrorPayload {
  error?: string;
  message?: string;
  retryAfterSeconds?: number;
}

/**
 * Raised for any non-2xx API response. `code` mirrors the server's `error`
 * field so callers can branch on semantics rather than HTTP status alone.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

/** Parses an error payload into an ApiError (shared with the SSE reader). */
export async function toError(response: Response): Promise<ApiError> {
  let payload: ApiErrorPayload = {};
  try {
    payload = (await response.json()) as ApiErrorPayload;
  } catch {
    // Non-JSON error bodies (e.g. proxy HTML) fall back to the status text.
  }
  return new ApiError(
    response.status,
    payload.error ?? 'unknown',
    payload.message ?? `Request failed (${response.status}).`,
    payload.retryAfterSeconds,
  );
}

let unauthorizedHandler: (() => void) | null = null;

/** Registers the app-wide "session is gone" reaction (return to login). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

interface RequestOptions {
  method?: string;
  /** JSON body — encoded automatically. */
  json?: unknown;
  /** Raw body (attachments, object PUT payloads). */
  raw?: Uint8Array | ArrayBuffer | string;
  contentType?: string;
  responseType?: ResponseKind;
  signal?: AbortSignal;
  /**
   * When true, a 401 does not trigger the global unauthorized handler.
   * Used for calls like vault unlock, where 401 means "wrong vault
   * password", not "session expired".
   */
  ignoreUnauthorized?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    // Session lives in an HttpOnly cookie; it must ride along on every call.
    credentials: 'include',
    signal: options.signal,
  };

  if (options.json !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(options.json);
  } else if (options.raw !== undefined) {
    init.headers = { 'Content-Type': options.contentType ?? 'application/octet-stream' };
    init.body = options.raw as BodyInit;
  }

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, init);
  } catch (error) {
    throw new ApiError(0, 'network', `Could not reach the server. ${(error as Error).message}`);
  }

  if (!response.ok) {
    const apiError = await toError(response);
    if (apiError.isUnauthorized && !options.ignoreUnauthorized) {
      unauthorizedHandler?.();
    }
    throw apiError;
  }

  switch (options.responseType ?? 'json') {
    case 'text':
      return (await response.text()) as T;
    case 'binary':
      return new Uint8Array(await response.arrayBuffer()) as T;
    default:
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
  }
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, json?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', json }),
  put: <T>(path: string, raw: Uint8Array | ArrayBuffer | string, contentType?: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', raw, contentType }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
};
